/**
 * Data Consistency Helpers
 * 
 * SINGLE SOURCE OF TRUTH for formatting data that appears across multiple pages.
 * Every page that shows posted time, member since, or seller ad counts MUST use
 * these functions — no local formatting, no fallbacks that change meaning.
 */
import { formatTimeAgo as _formatTimeAgo } from './timeAgo';

/**
 * Format the relative posted time for an ad.
 * Source of truth: ad.created_at from the backend (UTC ISO string).
 * 
 * Usage: formatRelativePostedTime(ad.created_at, 'en') → "2 hours ago"
 *        formatRelativePostedTime(ad.created_at, 'ar') → "قبل ساعتين"
 * 
 * @param {string} createdAt - ISO date string from backend (ad.created_at)
 * @param {string} locale - 'en' or 'ar'
 * @returns {string} Relative time string, or empty string if no date
 */
export function formatRelativePostedTime(createdAt, locale = 'en') {
  if (!createdAt) return '';
  return _formatTimeAgo(createdAt, locale);
}

/**
 * Format "Member since" date for a user/seller.
 * Source of truth: user.created_at from the backend (the account registration date).
 * 
 * NEVER use ad.created_at, profile_updated_at, or any other field as fallback.
 * If the value is missing, return empty string — do not guess.
 * 
 * Usage: formatMemberSince(seller.joined_at, 'en') → "Feb 2026"
 *        formatMemberSince(user.created_at, 'ar')  → "فبراير ٢٠٢٦"
 * 
 * @param {string} userCreatedAt - ISO date string from backend (user.created_at or seller.joined_at)
 * @param {string} locale - 'en' or 'ar'
 * @returns {string} Formatted "Month Year" string, or empty string if no date
 */
export function formatMemberSince(userCreatedAt, locale = 'en') {
  if (!userCreatedAt) return '';
  const d = new Date(userCreatedAt);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(d);
}

/**
 * Get the seller's active (published) ad count.
 * Source of truth: the backend field `active_listing_count` or `listing_count`.
 * Both fields use the same `get_public_ad_filter()` on the backend.
 * 
 * @param {object} seller - Seller object from API (ad.seller or /api/seller/:id)
 * @returns {number} Active listing count
 */
export function getSellerActiveAdCount(seller) {
  if (!seller) return 0;
  return seller.active_listing_count ?? seller.listing_count ?? 0;
}
